import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Popup, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers
const customIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
      <circle cx="12" cy="12" r="10" fill="#ff4444" stroke="white" stroke-width="2"/>
      <circle cx="12" cy="12" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

const parsePolygon = (polygonString) => {
  try {
    if (!polygonString || typeof polygonString !== 'string') {
      return null;
    }
    
    const coordString = polygonString
      .replace(/POLYGON\s*\(\(/i, '')
      .replace(/\)\)$/, '')
      .trim();
    
    if (!coordString) {
      return null;
    }
    
    const coords = coordString
      .split(',')
      .map(coord => {
        const parts = coord.trim().split(/\s+/);
        if (parts.length < 2) return null;
        
        const lng = parseFloat(parts[0]);
        const lat = parseFloat(parts[1]);
        
        if (isNaN(lng) || isNaN(lat)) {
          return null;
        }
        
        return [lng, lat]; // GeoJSON format: [lng, lat]
      })
      .filter(coord => coord !== null);
    
    if (coords.length < 3) {
      return null;
    }
    
    // Ensure polygon is closed
    const firstCoord = coords[0];
    const lastCoord = coords[coords.length - 1];
    if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
      coords.push([firstCoord[0], firstCoord[1]]);
    }
    
    return {
      type: "Polygon",
      coordinates: [coords]
    };
  } catch (error) {
    console.error('Error parsing polygon:', polygonString, error);
    return null;
  }
};

const getRandomColor = () => {
  const hue = Math.floor(Math.random() * 360);
  const saturation = Math.floor(Math.random() * 40) + 60; // 60-100%
  const lightness = Math.floor(Math.random() * 30) + 40; // 40-70%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const App = () => {
  const [coverageData, setCoverageData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    stations: [],
    types: [],
    product_types: [],
    units: []
  });
  const [filters, setFilters] = useState({
    name: '',
    product_type: '',
    station: '',
    type: '',
    unit: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [addressSearch, setAddressSearch] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchedLocation, setSearchedLocation] = useState(null);
  const [mapRef, setMapRef] = useState(null);



  const loadData = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetch('https://gqmzeyvooxehrod4hcdcefeffi0pbmwp.lambda-url.us-east-1.on.aws/');
      const data = await response.json();
      const { routes, assignments } = data;
      
      const processedData = routes
        .filter(route => route.polygon && route.name)
        .map(route => {
          const parsedPolygon = parsePolygon(route.polygon);
          if (!parsedPolygon) return null;
          
          const matchingAssignments = assignments.filter(assignment => 
            assignment.id_polygon === route.id && assignment.status === 'ACTIVE'
          );
          
          return {
            id: route.id,
            name: route.name,
            description: route.description || '',
            polygon: parsedPolygon,
            color: getRandomColor(),
            station: route.station || 'No station',
            product_type: route.product_type || '',
            type: route.type || '',
            units: [...new Set(matchingAssignments.map(assignment => assignment.unit).filter(Boolean))]
          };
        })
        .filter(item => item !== null);
      
      const stations = [...new Set(processedData.map(item => item.station).filter(Boolean))].sort();
      const types = [...new Set(processedData.map(item => item.type).filter(Boolean))].sort();
      const product_types = [...new Set(processedData.map(item => item.product_type).filter(Boolean))].sort();
      const units = [...new Set(processedData.flatMap(item => item.units).filter(Boolean))].sort();
      
      setFilterOptions({ stations, types, product_types, units });
      setAllData(processedData);
      setCoverageData(processedData);
    } catch (error) {
      console.error('Error loading data from API:', error);
      setError(true);
      setAllData([]);
      setCoverageData([]);
      setFilterOptions({ stations: [], types: [], product_types: [], units: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const searchAddress = async () => {
    if (!addressSearch.trim()) return;
    
    try {
      const apiKey = 'AIzaSyD0OzAhYffzzZFKHiwP4yzvoYUdVcbpDOA';
      const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(addressSearch)}&key=${apiKey}`);
      const data = await response.json();
      
      if (data.status === 'OK' && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        const point = [lng, lat];
        
        // Check which polygons contain this point
        const containingPolygons = allData.filter(item => {
          return isPointInPolygon(point, item.polygon.coordinates[0]);
        });
        
        const newPos = [lat, lng];
        
        setSearchedLocation({
          coordinates: newPos,
          address: data.results[0].formatted_address,
          coverage: containingPolygons
        });
        
        setSearchResult({
          coordinates: newPos,
          address: data.results[0].formatted_address,
          coverage: containingPolygons
        });
        
        // Map will be moved by MapController component
      } else {
        setSearchResult({ coordinates: null, address: 'Dirección no encontrada', coverage: [] });
        setSearchedLocation(null);
      }
    } catch (error) {
      console.error('Error searching address:', error);
      console.log('address', addressSearch);
      console.log('key', process.env.REACT_APP_MAPS_API_KEY);
      setSearchResult({ coordinates: null, address: 'Error en la búsqueda', coverage: [] });
      setSearchedLocation(null);
    }
  };



  const isPointInPolygon = (point, polygon) => {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  };





  const clearAllFilters = () => {
    setFilters({
      name: '',
      product_type: '',
      station: '',
      type: '',
      unit: ''
    });
    setAddressSearch('');
    setSearchResult(null);
    setSearchedLocation(null);
    
    // Reset units to show all
    const allUnits = [...new Set(allData.flatMap(item => item.units).filter(Boolean))].sort();
    setFilterOptions(prev => ({ ...prev, units: allUnits }));
    
    // Show all data
    setCoverageData(allData);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update units based on station and product_type filters
    if (key === 'station' || key === 'product_type') {
      const filteredUnits = allData
        .filter(item => {
          const stationMatch = newFilters.station === '' || item.station === newFilters.station;
          const productTypeMatch = newFilters.product_type === '' || item.product_type === newFilters.product_type;
          return stationMatch && productTypeMatch;
        })
        .flatMap(item => item.units)
        .filter(Boolean);
      
      const uniqueFilteredUnits = [...new Set(filteredUnits)].sort();
      setFilterOptions(prev => ({ ...prev, units: uniqueFilteredUnits }));
      
      // Reset unit filter if current unit is not available with current filters
      if (newFilters.unit && !uniqueFilteredUnits.includes(newFilters.unit)) {
        newFilters.unit = '';
      }
    }
    
    const filtered = allData.filter(item => {
      const nameMatch = newFilters.name === '' || 
        item.name.toLowerCase().includes(newFilters.name.toLowerCase()) ||
        item.description.toLowerCase().includes(newFilters.name.toLowerCase());
      
      return (
        nameMatch &&
        (newFilters.product_type === '' || item.product_type === newFilters.product_type) &&
        (newFilters.station === '' || item.station === newFilters.station) &&
        (newFilters.type === '' || item.type === newFilters.type) &&
        (newFilters.unit === '' || item.units.includes(newFilters.unit))
      );
    });
    setCoverageData(filtered);
  };

  // Map controller component to handle map movements
  const MapController = () => {
    const map = useMap();
    
    useEffect(() => {
      if (searchedLocation && searchedLocation.coordinates) {
        map.flyTo(searchedLocation.coordinates, 15, { duration: 2 });
      }
    }, [searchedLocation, map]);
    
    return null;
  };

  return (
    <div style={{ position: 'relative', height: '100vh' }}>
      {/* Loading Overlay */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(2px)'
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '12px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>⏳</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>Cargando datos...</div>
          </div>
        </div>
      )}
      {/* Filter Panel */}
      <div style={{
        position: 'absolute',
        top: '15px',
        right: '15px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        zIndex: 1000,
        minWidth: '250px',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h4 style={{ 
            margin: '0', 
            fontSize: '16px', 
            color: 'white',
            fontWeight: '600'
          }}>🗺️ Filtros</h4>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={clearAllFilters}
              title="Limpiar todos los filtros"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '6px',
                color: 'white',
                padding: '6px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              🗑️
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              title={error ? 'No se pudieron obtener las rutas' : 'Actualizar datos'}
              style={{
                background: error ? 'rgba(255,100,100,0.3)' : loading ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)',
                border: `1px solid ${error ? 'rgba(255,100,100,0.5)' : 'rgba(255,255,255,0.3)'}`,
                borderRadius: '6px',
                color: 'white',
                padding: '6px 10px',
                fontSize: '12px',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {loading ? '⏳' : error ? '⚠️' : '🔄'}
            </button>
          </div>
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <label style={{ color: 'white', fontSize: '12px', marginBottom: '4px', display: 'block' }}>Buscar Dirección</label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <div style={{ position: 'relative', flex: '1', minWidth: 0 }}>
              <input
                type="text"
                placeholder="Ingresa una dirección..."
                value={addressSearch}
                onChange={(e) => setAddressSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchAddress()}
                style={{ 
                  width: '100%',
                  padding: '8px 8px', 
                  fontSize: '12px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <button
              onClick={searchAddress}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '6px',
                color: 'white',
                padding: '8px 10px',
                fontSize: '12px',
                cursor: 'pointer',
                flexShrink: 0,
                minWidth: '36px'
              }}
            >
              🔍
            </button>
          </div>
          {searchResult && (
            <div style={{
              marginTop: '8px',
              padding: '8px',
              backgroundColor: searchResult.coverage.length > 0 ? 'rgba(0,255,0,0.1)' : 'rgba(255,0,0,0.1)',
              borderRadius: '6px',
              fontSize: '11px',
              color: 'white'
            }}>
              {searchResult.coordinates ? (
                <>
                  <div><strong>📍 Dirección encontrada</strong></div>
                  <div style={{ marginTop: '4px' }}>
                    {searchResult.coverage.length > 0 ? (
                      <>
                        <div style={{ color: '#90EE90' }}>✅ Tiene cobertura en:</div>
                        {searchResult.coverage.map((coverage, idx) => (
                          <div key={idx} style={{ marginLeft: '10px', fontSize: '10px' }}>
                            • {coverage.name} ({coverage.station})
                          </div>
                        ))}
                      </>
                    ) : (
                      <div style={{ color: '#FFB6C1' }}>❌ Sin cobertura disponible</div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ color: '#FFB6C1' }}>❌ {searchResult.address}</div>
              )}
            </div>
          )}
        </div>
        
        <div style={{ marginBottom: '12px' }}>
          <label style={{ color: 'white', fontSize: '12px', marginBottom: '4px', display: 'block' }}>Nombre/Descripción de Ruta</label>
          <input
            type="text"
            placeholder="Buscar por nombre o descripción..."
            value={filters.name}
            onChange={(e) => handleFilterChange('name', e.target.value)}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              fontSize: '13px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: 'rgba(255,255,255,0.9)',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>
        
        <div style={{ marginBottom: '12px' }}>
          <label style={{ color: 'white', fontSize: '12px', marginBottom: '4px', display: 'block' }}>Tipo de Producto</label>
          <select
            value={filters.product_type}
            onChange={(e) => handleFilterChange('product_type', e.target.value)}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              fontSize: '13px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: 'rgba(255,255,255,0.9)',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          >
            <option value="">Todos los Tipos de Producto</option>
            {filterOptions.product_types.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        
        <div style={{ marginBottom: '12px' }}>
          <label style={{ color: 'white', fontSize: '12px', marginBottom: '4px', display: 'block' }}>Estación</label>
          <select
            value={filters.station}
            onChange={(e) => handleFilterChange('station', e.target.value)}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              fontSize: '13px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: 'rgba(255,255,255,0.9)',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          >
            <option value="">Todas las Estaciones</option>
            {filterOptions.stations.map(station => (
              <option key={station} value={station}>{station}</option>
            ))}
          </select>
        </div>
        
        <div style={{ marginBottom: '12px' }}>
          <label style={{ color: 'white', fontSize: '12px', marginBottom: '4px', display: 'block' }}>Tipo</label>
          <select
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              fontSize: '13px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: 'rgba(255,255,255,0.9)',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          >
            <option value="">Todos los Tipos</option>
            {filterOptions.types.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        
        <div style={{ marginBottom: '12px' }}>
          <label style={{ color: 'white', fontSize: '12px', marginBottom: '4px', display: 'block' }}>Unidad</label>
          <select
            value={filters.unit}
            onChange={(e) => handleFilterChange('unit', e.target.value)}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              fontSize: '13px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: 'rgba(255,255,255,0.9)',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          >
            <option value="">Todas las Unidades</option>
            {filterOptions.units.map(unit => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </select>
        </div>
        
        <div style={{ 
          fontSize: '12px', 
          color: 'rgba(255,255,255,0.8)',
          textAlign: 'center',
          padding: '8px',
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderRadius: '6px'
        }}>
          📊 Mostrando {coverageData.length} de {allData.length} polígonos
        </div>
      </div>
      
      <MapContainer 
        center={[21.5020, -104.6200]} 
        zoom={13} 
        style={{ height: '100vh' }}
      >
        <MapController />
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        
        {searchedLocation && (
          <Marker position={searchedLocation.coordinates} icon={customIcon}>
            <Popup>
              <div>
                <strong>📍 Dirección encontrada</strong><br/>
                <small>{searchedLocation.address}</small><br/>
                {searchedLocation.coverage.length > 0 ? (
                  <>
                    <div style={{ color: 'green' }}>✅ Con cobertura en:</div>
                    {searchedLocation.coverage.map((coverage, idx) => (
                      <div key={idx} style={{ fontSize: '11px' }}>
                        • {coverage.name} ({coverage.station})
                      </div>
                    ))}
                  </>
                ) : (
                  <div style={{ color: 'red' }}>❌ Sin cobertura</div>
                )}
              </div>
            </Popup>
          </Marker>
        )}
        
        {coverageData.map((coverage, idx) => {
          const color = coverage.color;
          const center = coverage.polygon.coordinates[0].reduce(
            (acc, coord) => [acc[0] + coord[1], acc[1] + coord[0]], 
            [0, 0]
          ).map(sum => sum / coverage.polygon.coordinates[0].length);
          
          return (
            <div key={idx}>
              <GeoJSON 
                data={coverage.polygon}
                style={{ color, fillColor: color, fillOpacity: 0.3, weight: 2 }}
              >
                <Popup>
                  <b>{coverage.name}</b><br/>
                  {coverage.description}<br/>
                  <strong>Estación:</strong> {coverage.station}<br/>
                  <strong>Unidades ({coverage.units?.length || 0}):</strong><br/>
                  <div dangerouslySetInnerHTML={{
                    __html: coverage.units?.map(unit => `🚛 ${unit}`).join('<br/>') || 'Sin unidades asignadas'
                  }} />
                </Popup>
              </GeoJSON>
              
              <CircleMarker
                center={center}
                radius={0}
                opacity={0}
              >
                <Popup permanent className="polygon-label">
                  <div style={{ 
                    background: 'white', 
                    padding: '4px 8px', 
                    borderRadius: '5px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    color: color,
                    border: `2px solid ${color}`,
                    minWidth: '120px'
                  }}>
                    <div>{coverage.name}</div>
                    <div style={{ fontSize: '10px', marginTop: '2px', fontWeight: 'normal' }}>
                      🚛 {coverage.units?.join(', ') || 'Sin unidades'}
                    </div>
                    <div style={{ fontSize: '10px', marginTop: '1px', fontWeight: 'normal', color: '#666' }}>
                      🏢 {coverage.station}
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            </div>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default App;